import { useState, useEffect, useCallback, useRef } from "react";
import { InfoClient, SubscriptionClient } from "../sdk";
import { ConnectionState } from "../sdk/types/transport";

export interface OrderbookConfig {
  baseURL: string;
  wsURL: string;
  marketId: string;
  maxDepth?: number;
  authToken?: string;
  walletAddress?: string;
  deviceID?: string;
}

export interface OrderbookLevel {
  price: number;
  size: number;
  cumulative: number;
  flash?: "up" | "down" | null; // Visual indicator for changed levels
}

export interface OrderbookData {
  asks: OrderbookLevel[];
  bids: OrderbookLevel[];
  spread?: number;
  midPrice?: number;
}

/**
 * Hook for managing orderbook data with HTTP initialization and WebSocket delta updates
 * Handles data cache management, sorting, and delta updates
 */
export function useOrderbook(config: OrderbookConfig) {
  const [orderbook, setOrderbook] = useState<OrderbookData>({
    asks: [],
    bids: [],
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [wsError, setWsError] = useState<string | null>(null);

  const infoClientRef = useRef<InfoClient | null>(null);
  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const updateQueueRef = useRef<any[]>([]);
  const debounceTimerRef = useRef<number | null>(null);
  const isSubscribedRef = useRef<boolean>(false);
  const currentMarketIdRef = useRef<string | null>(null);
  const pollingIntervalRef = useRef<number | null>(null);
  const connectionTimeoutRef = useRef<number | null>(null);

  /**
   * Convert price-size array to OrderbookLevel with cumulative depth
   * Both asks and bids sorted descending (highest to lowest, top to bottom)
   */
  const processLevels = useCallback(
    (levels: [number, number][]): OrderbookLevel[] => {
      // Sort: both asks and bids descending (high to low, big to small)
      const sorted = [...levels].sort((a, b) => {
        return b[0] - a[0]; // Descending: highest price first
      });

      // Calculate cumulative depth
      let cumulative = 0;
      return sorted.map(([price, size]) => {
        cumulative += size;
        return {
          price,
          size,
          cumulative,
          flash: null,
        };
      });
    },
    [],
  );

  /**
   * Apply delta update to orderbook with flash indicators
   * Delta format: { bids: [[price, size]], asks: [[price, size]] }
   * Size 0 means remove the level
   */
  const applyDelta = useCallback(
    (
      current: OrderbookData,
      delta: { bids?: [number, number][]; asks?: [number, number][] },
    ): OrderbookData => {
      const newAsks = new Map<number, number>();
      const newBids = new Map<number, number>();
      const askChanges = new Map<number, "up" | "down">();
      const bidChanges = new Map<number, "up" | "down">();

      // Initialize with current levels
      current.asks.forEach((level) => {
        if (level.size > 0) {
          newAsks.set(level.price, level.size);
        }
      });
      current.bids.forEach((level) => {
        if (level.size > 0) {
          newBids.set(level.price, level.size);
        }
      });

      // Apply delta updates and track changes
      if (delta.asks) {
        delta.asks.forEach(([price, size]) => {
          const oldSize = newAsks.get(price) || 0;
          if (size === 0) {
            newAsks.delete(price);
          } else {
            if (oldSize > 0) {
              askChanges.set(price, size > oldSize ? "up" : "down");
            } else {
              askChanges.set(price, "up"); // New level
            }
            newAsks.set(price, size);
          }
        });
      }

      if (delta.bids) {
        delta.bids.forEach(([price, size]) => {
          const oldSize = newBids.get(price) || 0;
          if (size === 0) {
            newBids.delete(price);
          } else {
            if (oldSize > 0) {
              bidChanges.set(price, size > oldSize ? "up" : "down");
            } else {
              bidChanges.set(price, "up"); // New level
            }
            newBids.set(price, size);
          }
        });
      }

      // Convert back to arrays and process
      const asksArray: [number, number][] = Array.from(newAsks.entries());
      const bidsArray: [number, number][] = Array.from(newBids.entries());

      const processedAsks = processLevels(asksArray);
      const processedBids = processLevels(bidsArray);

      // Apply flash indicators
      processedAsks.forEach((level) => {
        level.flash = askChanges.get(level.price) || null;
      });
      processedBids.forEach((level) => {
        level.flash = bidChanges.get(level.price) || null;
      });

      // Limit depth if specified
      const maxDepth = config.maxDepth || 100;
      const limitedAsks = processedAsks.slice(0, maxDepth);
      const limitedBids = processedBids.slice(0, maxDepth);

      // Calculate spread and mid price
      // Both asks and bids are descending (first is highest, last is lowest)
      let spread: number | undefined;
      let midPrice: number | undefined;
      if (limitedAsks.length > 0 && limitedBids.length > 0) {
        const bestAsk = limitedAsks[limitedAsks.length - 1].price; // Lowest ask (last in descending)
        const bestBid = limitedBids[0].price; // Highest bid (first in descending)
        spread = bestAsk - bestBid;
        midPrice = (bestAsk + bestBid) / 2;
      }

      return {
        asks: limitedAsks,
        bids: limitedBids,
        spread,
        midPrice,
      };
    },
    [processLevels, config.maxDepth],
  );

  /**
   * Poll orderbook from HTTP request (for fallback when WebSocket fails)
   * Doesn't set loading state to avoid UI flicker during polling
   */
  const pollOrderbook = useCallback(async () => {
    if (!infoClientRef.current) {
      return;
    }

    try {
      const data = await infoClientRef.current.getOrderbook(
        config.marketId,
        true,
      );

      if (data && (data.bids || data.asks)) {
        const asksArray: [number, number][] = data.asks || [];
        const bidsArray: [number, number][] = data.bids || [];

        const processedAsks = processLevels(asksArray);
        const processedBids = processLevels(bidsArray);

        // Limit depth if specified
        const maxDepth = config.maxDepth || 100;
        const limitedAsks = processedAsks.slice(0, maxDepth);
        const limitedBids = processedBids.slice(0, maxDepth);

        // Calculate spread and mid price
        // Both asks and bids are descending (first is highest, last is lowest)
        let spread: number | undefined;
        let midPrice: number | undefined;
        if (limitedAsks.length > 0 && limitedBids.length > 0) {
          const bestAsk = limitedAsks[limitedAsks.length - 1].price; // Lowest ask (last in descending)
          const bestBid = limitedBids[0].price; // Highest bid (first in descending)
          spread = bestAsk - bestBid;
          midPrice = (bestAsk + bestBid) / 2;
        }

        setOrderbook({
          asks: limitedAsks,
          bids: limitedBids,
          spread,
          midPrice,
        });
      }
    } catch (err) {
      // Only set error if we don't have existing data (to avoid overwriting with transient errors)
      setError(err instanceof Error ? err.message : "Failed to load orderbook");
    }
  }, [config.marketId, config.maxDepth, processLevels]);

  /**
   * Initialize orderbook from HTTP request
   */
  const initializeOrderbook = useCallback(async () => {
    if (!infoClientRef.current) return;

    setLoading(true);
    setError(null);

    try {
      const data = await infoClientRef.current.getOrderbook(
        config.marketId,
        true,
      );

      if (data && (data.bids || data.asks)) {
        const asksArray: [number, number][] = data.asks || [];
        const bidsArray: [number, number][] = data.bids || [];

        const processedAsks = processLevels(asksArray);
        const processedBids = processLevels(bidsArray);

        // Limit depth if specified
        const maxDepth = config.maxDepth || 100;
        const limitedAsks = processedAsks.slice(0, maxDepth);
        const limitedBids = processedBids.slice(0, maxDepth);

        // Calculate spread and mid price
        // Both asks and bids are descending (first is highest, last is lowest)
        let spread: number | undefined;
        let midPrice: number | undefined;
        if (limitedAsks.length > 0 && limitedBids.length > 0) {
          const bestAsk = limitedAsks[limitedAsks.length - 1].price; // Lowest ask (last in descending)
          const bestBid = limitedBids[0].price; // Highest bid (first in descending)
          spread = bestAsk - bestBid;
          midPrice = (bestAsk + bestBid) / 2;
        }

        setOrderbook({
          asks: limitedAsks,
          bids: limitedBids,
          spread,
          midPrice,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load orderbook");
    } finally {
      setLoading(false);
    }
  }, [config.marketId, config.maxDepth, processLevels]);

  /**
   * Process orderbook update (snapshot or delta)
   */
  const processOrderbookUpdate = useCallback(
    (data: any, current: OrderbookData): OrderbookData => {
      // Check for explicit snapshot flag or infer from message structure
      const isSnapshot =
        data.snapshot === true ||
        data.type === "snapshot" ||
        (data.bids &&
          data.asks &&
          data.bids.length > 10 &&
          data.asks.length > 10 &&
          !data.delta);

      if (isSnapshot) {
        // Full snapshot - replace entire orderbook
        const asksArray: [number, number][] = data.asks || [];
        const bidsArray: [number, number][] = data.bids || [];

        const processedAsks = processLevels(asksArray);
        const processedBids = processLevels(bidsArray);

        const maxDepth = config.maxDepth || 100;
        const limitedAsks = processedAsks.slice(0, maxDepth);
        const limitedBids = processedBids.slice(0, maxDepth);

        // Calculate spread and mid price
        // Both asks and bids are descending (first is highest, last is lowest)
        let spread: number | undefined;
        let midPrice: number | undefined;
        if (limitedAsks.length > 0 && limitedBids.length > 0) {
          const bestAsk = limitedAsks[limitedAsks.length - 1].price; // Lowest ask (last in descending)
          const bestBid = limitedBids[0].price; // Highest bid (first in descending)
          spread = bestAsk - bestBid;
          midPrice = (bestAsk + bestBid) / 2;
        }

        return {
          asks: limitedAsks,
          bids: limitedBids,
          spread,
          midPrice,
        };
      } else {
        // Delta update - merge with current state
        return applyDelta(current, {
          bids: data.bids,
          asks: data.asks,
        });
      }
    },
    [processLevels, applyDelta, config.maxDepth],
  );

  /**
   * Handle WebSocket orderbook update with debouncing
   */
  const handleOrderbookUpdate = useCallback(
    (data: any) => {
      if (!data || (!data.bids && !data.asks)) return;

      // Queue the update
      updateQueueRef.current.push(data);

      // Clear existing debounce timer
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce updates to prevent UI thrashing (100ms throttle)
      debounceTimerRef.current = window.setTimeout(() => {
        // Process all queued updates in batch
        setOrderbook((current) => {
          let result = current;
          for (const update of updateQueueRef.current) {
            result = processOrderbookUpdate(update, result);
          }
          updateQueueRef.current = [];
          
          // Compare with current state to avoid unnecessary updates
          // For orderbook, compare key metrics (length, best bid/ask) instead of full JSON
          if (current.asks.length === result.asks.length && 
              current.bids.length === result.bids.length &&
              current.spread === result.spread &&
              current.midPrice === result.midPrice) {
            // Check if best bid/ask prices changed
            const currentBestBid = current.bids.length > 0 ? current.bids[0].price : 0;
            const currentBestAsk = current.asks.length > 0 ? current.asks[current.asks.length - 1].price : 0;
            const resultBestBid = result.bids.length > 0 ? result.bids[0].price : 0;
            const resultBestAsk = result.asks.length > 0 ? result.asks[result.asks.length - 1].price : 0;
            
            if (currentBestBid === resultBestBid && currentBestAsk === resultBestAsk) {
              // No meaningful change, return current to prevent re-render
              return current;
            }
          }
          
          return result;
        });
        debounceTimerRef.current = null;
      }, 100);
    },
    [processOrderbookUpdate],
  );

  // Initialize clients (only when connection config changes, not marketId)
  useEffect(() => {
    const infoClient = new InfoClient({
      baseURL: config.baseURL,
    });
    infoClientRef.current = infoClient;

    const subscriptionClient = new SubscriptionClient({
      url: config.wsURL,
      authToken: config.authToken,
      walletAddress: config.walletAddress,
      deviceID: config.deviceID,
    });
    subscriptionClientRef.current = subscriptionClient;

    // Initialize market ID ref on mount
    if (currentMarketIdRef.current === null) {
      currentMarketIdRef.current = config.marketId;
    }

    // Subscribe function that can be called on initial connect or reconnection
    // Uses currentMarketIdRef to always get the latest market ID
    const subscribe = () => {
      if (subscriptionClient.isConnected()) {
        const marketIdToSubscribe =
          currentMarketIdRef.current !== null
            ? currentMarketIdRef.current
            : config.marketId; // Fallback to config.marketId on initial mount

        // Unsubscribe from previous market if different
        if (
          currentMarketIdRef.current !== null &&
          currentMarketIdRef.current !== marketIdToSubscribe
        ) {
          subscriptionClient.unsubscribeOrderbook(
            currentMarketIdRef.current,
            handleOrderbookUpdate,
          );
        }
        // Subscribe to current market
        if (
          !isSubscribedRef.current ||
          currentMarketIdRef.current !== marketIdToSubscribe
        ) {
          subscriptionClient.subscribeOrderbook(
            marketIdToSubscribe,
            handleOrderbookUpdate,
            true,
          );
          isSubscribedRef.current = true;
          currentMarketIdRef.current = marketIdToSubscribe;
        }
      }
    };

    // Connect WebSocket and subscribe
    const connectPromise = subscriptionClient.connect();

    // Add a timeout fallback in case the promise never rejects
    // This ensures polling starts even if WebSocketClient doesn't properly reject
    connectionTimeoutRef.current = window.setTimeout(() => {
      const isActuallyConnected = subscriptionClient.isConnected();
      if (
        !isActuallyConnected &&
        infoClientRef.current &&
        pollingIntervalRef.current === null
      ) {
        setWsError("WebSocket connection timeout. Using HTTP polling.");
        setIsConnected(false);
        pollOrderbook();
        pollingIntervalRef.current = window.setInterval(() => {
          pollOrderbook();
        }, 5000);
      }
    }, 12000); // 12 seconds (2 seconds after WebSocketClient's 10-second timeout)

    connectPromise
      .then(() => {
        if (connectionTimeoutRef.current !== null) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        setIsConnected(true);
        setWsError(null); // Clear WebSocket error on successful connection
        subscribe();
      })
      .catch(() => {
        if (connectionTimeoutRef.current !== null) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        setWsError(`slow connection due to network issue`);
        setIsConnected(false);
        // Explicitly start polling when WebSocket fails
        // Since isConnected was already false, the polling effect won't re-run
        if (infoClientRef.current) {
          // Clear any existing polling interval first
          if (pollingIntervalRef.current !== null) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          // Start polling immediately, then every 5 seconds
          pollOrderbook();
          pollingIntervalRef.current = window.setInterval(() => {
            pollOrderbook();
          }, 5000);
        }
      });

    // Monitor connection state changes and re-subscribe on reconnection
    let unsubscribeStateChange: (() => void) | null = null;
    const wsClient = (subscriptionClient as any).wsClient;
    if (wsClient) {
      unsubscribeStateChange = wsClient.onStateChange(
        (state: ConnectionState) => {
          if (state === ConnectionState.Connected) {
            setIsConnected(true);
            setWsError(null); // Clear WebSocket error on successful connection
            // Re-subscribe if we were previously subscribed
            if (isSubscribedRef.current) {
              subscribe();
            }
          } else if (
            state === ConnectionState.Disconnected ||
            state === ConnectionState.Reconnecting
          ) {
            setIsConnected(false);
            isSubscribedRef.current = false;
            // Set WebSocket error on disconnect (component will only show if data exists)
            setWsError("WebSocket disconnected. Using static data.");
          }
        },
      );
    }

    return () => {
      // Clear debounce timer
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      // Clear connection timeout
      if (connectionTimeoutRef.current !== null) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      updateQueueRef.current = [];
      isSubscribedRef.current = false;
      currentMarketIdRef.current = null;

      // Unsubscribe from state changes
      if (unsubscribeStateChange) {
        unsubscribeStateChange();
      }

      if (subscriptionClientRef.current) {
        // Unsubscribe from current market if subscribed
        if (currentMarketIdRef.current !== null) {
          subscriptionClientRef.current.unsubscribeOrderbook(
            currentMarketIdRef.current,
            handleOrderbookUpdate,
          );
        }
        subscriptionClientRef.current.disconnect();
      }
    };
  }, [
    config.baseURL,
    config.wsURL,
    config.authToken,
    config.walletAddress,
    config.deviceID,
  ]);

  // Handle market ID changes: re-initialize orderbook and re-subscribe to WS
  useEffect(() => {
    if (!infoClientRef.current || !subscriptionClientRef.current) {
      return;
    }

    const previousMarketId = currentMarketIdRef.current;

    // Unsubscribe from previous market if it exists and is different
    if (
      previousMarketId !== null &&
      previousMarketId !== config.marketId &&
      subscriptionClientRef.current.isConnected()
    ) {
      subscriptionClientRef.current.unsubscribeOrderbook(
        previousMarketId,
        handleOrderbookUpdate,
      );
      isSubscribedRef.current = false;
    }

    // Clear existing orderbook data when switching markets
    setOrderbook({
      asks: [],
      bids: [],
    });
    setLoading(true);
    setError(null);
    setWsError(null);

    // Initialize new market orderbook from REST
    initializeOrderbook();

    // Subscribe to new market WebSocket updates
    if (subscriptionClientRef.current.isConnected()) {
      subscriptionClientRef.current.subscribeOrderbook(
        config.marketId,
        handleOrderbookUpdate,
        true,
      );
      isSubscribedRef.current = true;
    }

    // Update ref to track current market ID
    currentMarketIdRef.current = config.marketId;

    // Cleanup function: unsubscribe when marketId changes or component unmounts
    return () => {
      if (
        subscriptionClientRef.current &&
        subscriptionClientRef.current.isConnected() &&
        currentMarketIdRef.current === config.marketId
      ) {
        subscriptionClientRef.current.unsubscribeOrderbook(
          config.marketId,
          handleOrderbookUpdate,
        );
        isSubscribedRef.current = false;
      }
    };
  }, [config.marketId, initializeOrderbook, handleOrderbookUpdate]);

  // Fallback to HTTP polling when WebSocket is disconnected
  useEffect(() => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current !== null) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Start polling if WebSocket is not connected
    if (!isConnected && infoClientRef.current) {
      // Start polling immediately, then every 5 seconds
      pollOrderbook();
      pollingIntervalRef.current = window.setInterval(() => {
        pollOrderbook();
      }, 5000);
    }

    return () => {
      if (pollingIntervalRef.current !== null) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isConnected, config.marketId, pollOrderbook]);

  // Clear flash indicators after display duration
  useEffect(() => {
    const flashTimer = setInterval(() => {
      setOrderbook((current) => ({
        ...current,
        asks: current.asks.map((level) => ({ ...level, flash: null })),
        bids: current.bids.map((level) => ({ ...level, flash: null })),
      }));
    }, 500); // Clear flashes after 500ms

    return () => clearInterval(flashTimer);
  }, []);

  return {
    orderbook,
    loading,
    error,
    isConnected,
    wsError,
    refresh: initializeOrderbook,
  };
}
